import { z } from 'zod';

/**
 * Foreman-submitted material request. MVP stores items as an
 * unstructured array on the request itself — Wave 4 will normalise
 * into a catalog-backed table.
 */
export const purchaseRequestStatusSchema = z.enum([
  'draft',
  'pending',
  'approved',
  'rejected',
  'fulfilled',
  'cancelled',
]);
export type PurchaseRequestStatus = z.infer<typeof purchaseRequestStatusSchema>;

export const purchaseRequestItemSchema = z.object({
  description: z.string().trim().min(1).max(200),
  quantity: z.number().min(0.001).max(999_999),
  unit: z.string().trim().min(1).max(20),
  estimatedUnitPriceCents: z.number().int().min(0).optional(),
  // Wave 379H · ICO refs (índice de composição de custo). One material
  // line can apply to multiple budget refs, e.g. ICO "1.7/3.1" → ["1.7","3.1"].
  // Used by quotation→PO close-out to resolve `budget_item_id` from
  // `budget_items.ref`. Optional and free-form — values are
  // normalized strings, not joined to budget_items at request time.
  icoRefs: z.array(z.string().trim().max(32)).optional(),
  // Wave 379H · onde o material vai ser usado (campo APLICAÇÃO da
  // planilha de pedido — "Piso deck", "Lavabo", "Pórtico"). Free text.
  application: z.string().trim().max(200).optional(),
  // Wave 379H · data de entrega solicitada (campo DATA DE ENTREGA).
  // ISO date string ("2026-03-21") or free-form ("21/mar"). Free text
  // for now; parser resolves to ISO if possible.
  requestedDeliveryAt: z.string().trim().max(40).optional(),
});
export type PurchaseRequestItem = z.infer<typeof purchaseRequestItemSchema>;

// Wave 379H · pedido de material xlsx import. Browser-side SheetJS
// parses the spreadsheet (same pattern as budget import) and POSTs a
// single `purchase_request` row with N items_json entries. PM does
// NOT enter prices — those come from quotation responses later.
export const importPurchaseRequestRequestSchema = z.object({
  title: z.string().trim().min(2).max(160),
  items: z.array(purchaseRequestItemSchema).min(1).max(500),
  note: z.string().trim().max(2000).optional(),
});
export type ImportPurchaseRequestRequest = z.infer<typeof importPurchaseRequestRequestSchema>;

export const purchaseRequestSchema = z.object({
  id: z.string(),
  projectId: z.string(),
  code: z.string(),
  title: z.string(),
  status: purchaseRequestStatusSchema,
  items: z.array(purchaseRequestItemSchema),
  totalEstimateCents: z.number().int().nullable(),
  note: z.string().nullable(),
  requestedBy: z.string(),
  requestedByName: z.string(),
  approvedBy: z.string().nullable(),
  approvedByName: z.string().nullable(),
  approvedAt: z.string().nullable(),
  rejectedReason: z.string().nullable(),
  fulfilledAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type PurchaseRequest = z.infer<typeof purchaseRequestSchema>;

export const createPurchaseRequestRequestSchema = z.object({
  title: z.string().trim().min(2).max(160),
  items: z.array(purchaseRequestItemSchema).min(1).max(100),
  note: z.string().trim().max(2000).optional(),
});
export type CreatePurchaseRequestRequest = z.infer<
  typeof createPurchaseRequestRequestSchema
>;

export const decisionPurchaseRequestRequestSchema = z.object({
  reason: z.string().trim().max(2000).optional(),
});
export type DecisionPurchaseRequestRequest = z.infer<
  typeof decisionPurchaseRequestRequestSchema
>;
